import * as SelectPrimitive from '@radix-ui/react-select';
import { PlatformList, platform } from '@visiba/std';
import { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { Icon, type IconType } from '../../icon';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { Label } from '../base/label.component';
import { theme as commonTheme } from '../common_input.theme';
import { CellulaInputSelectContext } from './_input_select.context';
import { CellulaInputSelectContent } from './_input_select_content.component';
import { CellulaInputSelectTrigger, Variant } from './_input_select_trigger.component';
import { theme } from './input_select.theme';
import { useInputSelect } from './use_input_select.hook';

export const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
	`,
	labelWrapper: styled.div<{ $hideLabel: boolean }>`
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: ${coreTokens.size.x1};
		margin-bottom: ${({ $hideLabel }) => ($hideLabel ? '0' : commonTheme.getValue('labelMarginBottom'))};
	`,
	description: styled(Description)`
		margin-bottom: ${commonTheme.getValue('descriptionMarginBottom')};
	`,
	viewport: styled(SelectPrimitive.Viewport)`
		padding: ${theme.getValue('itemViewportPaddingMobile')};

		${coreTokens.breakpointQuery.medium} {
			padding: ${theme.getValue('itemViewportPadding')};
		}
	`,
	scrollUpButton: styled(SelectPrimitive.ScrollUpButton)`
		display: flex;
		align-items: center;
		justify-content: center;
		height: 25px;
		background-color: white;
		color: ${theme.getValue('itemScrollButtonColor')};
		cursor: default;
	`,
	scrollDownButton: styled(SelectPrimitive.ScrollDownButton)`
		display: flex;
		align-items: center;
		justify-content: center;
		height: 25px;
		background-color: white;
		color: ${theme.getValue('itemScrollButtonColor')};
		cursor: default;
	`,
};

export interface InputSelectRootProps extends SelectPrimitive.SelectProps {
	/**
	 * The label of the selectbox.
	 */
	label: string;

	/**
	 * Name
	 */
	name?: string;

	/**
	 * Use to visually hide the label.
	 */
	hideLabel?: boolean;

	/**
	 * Use to add a descriptive text below the selectbox.
	 */
	helperText?: string;

	/**
	 * Use to indicate if the input field has invalid input or not.
	 */
	invalid?: boolean;

	/**
	 * It's mandatory if `invalid` is set to `true` and only visible in that case
	 * too. The purpose with this is to provide a explanation why the selectbox is
	 * `invalid`. This text will replace the `helperText`, right below the text
	 * area.
	 */
	invalidText?: string;

	/**
	 * Reflects the value of the native HTML Input `id` property.
	 */
	id?: string;

	/**
	 * Use to make the selectbox read only.
	 */
	readOnly?: boolean;

	/**
	 * Use to indicate if the selectbox is mandatory or not.
	 */
	required?: boolean;
	placeholder?: string;
	value?: string;
	onValueChange?(value: string): void;
	description?: string;
	options?: RegisterOptions;
	optional?: boolean;
	labelSlot?: React.ReactNode;

	/**
	 * An optional leading icon rendered inside the select trigger.
	 */
	icon?: IconType;

	/**
	 * Visual variant of the select trigger.
	 * - 'default': Standard bordered style
	 * - 'ghost': Borderless style with highlighted colors
	 */
	variant?: Variant;
}

export const CellulaInputSelectRoot = forwardRef(function CellulaInputSelectRoot(
	{
		label,
		hideLabel = false,
		name,
		required = false,
		id,
		invalid,
		invalidText,
		helperText,
		placeholder,
		readOnly,
		onValueChange,
		options,
		children,
		value,
		description,
		optional,
		open,
		labelSlot,
		icon,
		variant = 'default',
		...rest
	}: InputSelectRootProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const { props, labelProps, triggerProps, descriptionProps, helpTextProps } = useInputSelect({
		id,
		name,
		label,
		hideLabel,
		invalid,
		invalidText,
		helperText,
		onValueChange,
		readOnly,
		required,
		options,
		value,
		placeholder,
		description,
		defaultValue: rest.defaultValue,
		optional,
	});

	const [isOpen, setIsOpen] = useState(open ?? false);
	const isMobile = platform.platformIs(PlatformList.Mobile);

	useEffect(
		function whenOpenChanges() {
			setIsOpen(open ?? false);
		},
		[open],
	);

	function handleOnChange(value: string): void {
		props.onValueChange?.(value);
	}

	function handleOpenChange(open: boolean): void {
		setIsOpen(open);
	}

	return (
		<CellulaInputSelectContext.Provider
			value={{
				readOnly: props.readOnly ?? false,
				invalid: props.invalid ?? false,
				isOpen,
				setIsOpen,
			}}
		>
			<SelectPrimitive.Root {...rest} {...props} open={isOpen} onValueChange={handleOnChange} onOpenChange={handleOpenChange}>
				<elements.container>
					<elements.labelWrapper $hideLabel={hideLabel}>
						<Label {...labelProps} />
						{labelSlot ?? null}
					</elements.labelWrapper>
					<elements.description {...descriptionProps} />
					{/* Bugfix to avoid click when scrolling: https://github.com/radix-ui/primitives/issues/1912#issuecomment-1822293506 */}
					<CellulaInputSelectTrigger
						{...(isMobile
							? {
									onPointerDown: (e) => e.preventDefault(),
									onClick: () => setIsOpen(!isOpen),
								}
							: undefined)}
						{...triggerProps}
						icon={icon}
						variant={variant}
						ref={ref}
					/>
					<HelpText {...helpTextProps} />
				</elements.container>
				<CellulaInputSelectContent labelProps={labelProps}>
					<elements.scrollUpButton>
						<Icon type='chevronUp' />
					</elements.scrollUpButton>
					<elements.viewport>{children}</elements.viewport>
					<elements.scrollDownButton>
						<Icon type='chevronDown' />
					</elements.scrollDownButton>
				</CellulaInputSelectContent>
			</SelectPrimitive.Root>
		</CellulaInputSelectContext.Provider>
	);
});
